// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../../common/api';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import resource from '../../../../share/util/resourceMap';
import util from '../../../../share/util/utils';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import cancelCodeFfd from '../../../vue/FFDModalCancelCode';
import DiagSystemUtils from '../../../../common/nodejs/diagSystemUtils';
import apiService from '../../../../share/util/api/service';
import { getAbbrSysName } from '../../../../share/util/systemName';
import { isAdas2CancelCode } from '../../../organisms/individualCancelCode/CancelCodeUtil';

const DIAG_SYSTEM = 'diagSytem.';
export default {
  name: "cancelCodePage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false },
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      measuredDataItems: [],
      systemId: undefined,
      projectId: undefined,
      measuredId: undefined,
      selectedCancelCode: undefined,
      ffdTitle: '',
      ffdSystem: '',
      ffdDescription: '',
      ffdCode: '',
      ffdTableItems: [],
      dataPlayback: true,
      cancelCodeFfdModal: false,
      i18nCncKey: "",
      displayCncFunc: null,
      cancelCodeId: undefined,
    };
  },

  watch: {
    // DataRecollection.js の setupTreeView() 内で言語切替を行っているため、処理無し
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
    'cancelCode-ffd-modal': cancelCodeFfd,
  },

  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('cache', ['cacheFFDStores']),

    /**
     * キャンセルコード計測データ一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     * @param {boolean} isSpecial 特殊機能フラグ
     */
    async createCncMeasuresList(projectId, measuresId, system, isSpecial) {
      try {
        this.processing = true;
        this.projectId = projectId;
        this.measuredId = measuresId;
        const params = { region_info: this.cachedProjectDataSource };
        const cancelCodeEndPoint = '/cancelCodes/' + projectId + '/' + measuresId;
        const resCancelCode = await api.getParamCall(config.CNC, cancelCodeEndPoint, params, this.operationScreen);
        this.systemId = system;

        handler.validate(handler.validateTypes.all, resCancelCode, this, null, () => {
          this.i18nCncKey = `cnc${system}.${resCancelCode.data.project.cancel_code_id}`;
          this.recollectedData = resCancelCode.data.project;
          this.measuredDataItems = [];
          this.displayCncFunc = null;
          this.cancelCodeId = resCancelCode.data.project.cancel_code_id;
          if (DiagSystemUtils.isEyesightByAbbreviationId(getAbbrSysName(system))) {
            // ESの場合
            const key = `${this.i18nCncKey}.menu_title`;
            this.displayCncFunc = this.$te(key) ? this.$t(key) : undefined;
            if (isSpecial) {
              // 特殊機能の場合
              this.updateTableSPES();
              this.updateColumnsSPES();
            } else {
              this.updateTableES();
              this.updateColumnsES();
            }
          } else if (DiagSystemUtils.isAdasEcuByAbbreviationId(getAbbrSysName(system))) {
            // ADASの場合
            const key = `${this.i18nCncKey}.menu_title`;
            this.displayCncFunc = this.$te(key) ? this.$t(key) : undefined;
            if (isAdas2CancelCode(this.cancelCodeId)) {
              // 不変IDが1002(追従クルコンキャンセルコード)の場合ADAS2の項目で表示
              if (isSpecial) {
                //特殊機能の場合
                this.updateTableSPADAS2();
                this.updateColumnsSPADAS2();
              } else {
                // 通常機能の場合
                this.updateTableADAS2();
                this.updateColumnsADAS2();
              }
            } else {
              // 上記以外の場合ADAS1の項目で表示
              if (isSpecial) {
                // 特殊機能の場合
                this.updateTableSPADAS();
                this.updateColumnsSPADAS();
              } else {
                this.updateTableADAS();
                this.updateColumnsADAS();
              }
            }
          } else if (DiagSystemUtils.isReverseBrakeAssistByAbbreviationId(getAbbrSysName(system))) {
            // RABの場合
            this.updateTableRAB();
            this.updateColumnsRAB();
          } else if (DiagSystemUtils.isTransmissionByAbbreviationId(getAbbrSysName(system))) {
            // トランスミッションの場合、「指定外コード」のレコードを非表示とする。
            this.updateTableTM();
            this.updateColumns();
          } else {
            // 上記以外の場合
            this.updateTable();
            this.updateColumns();
          }
          // マウスカーソル変更
          this.measuredDataItems.map(e => {
            e.bgStyle = 'dtc-cursor-default';
          });
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /**
     * テーブル情報を更新する
     */
    updateTable() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTable(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTable(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(TM)
     */
    updateTableTM() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableTM(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTable(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(RAB)
     */
    updateTableRAB() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableRAB(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTableRAB(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(ES)
     */
    updateTableES() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableES(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTableES(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(ADAS)
     */
    updateTableADAS() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableADAS1(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTableADAS1(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(ADAS2)
     */
    updateTableADAS2() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableADAS2(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTableADAS2(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(特殊機能ES)
     */
    updateTableSPES() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createSpCancelCodeResultTable(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptySpCancelCodeResultTable(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(特殊機能ADAS)
     */
    updateTableSPADAS() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createSpCancelCodeResultTable(this.recollectedData.data, this, this.i18nCncKey, true));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptySpCancelCodeResultTable(this));
          break;
        default:
          break;
      }
    },

    /**
     * テーブル情報を更新する(特殊機能ADAS2)
     */
    updateTableSPADAS2() {
      switch (this.recollectedData.res_type) {
        case config.CNC_RESPONSE_TYPE_OK:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createCancelCodeResultTableSPADAS2(this.recollectedData.data, this, this.i18nCncKey));
          break;
        case config.CNC_RESPONSE_TYPE_NG:
        case config.CNC_RESPONSE_TYPE_NO:
          this.measuredDataItems = this.measuredDataItems.concat(resource.createEmptyCancelCodeResultTableSPADAS2(this));
          break;
        default:
          break;
      }
    },

    /** 初期表示で必要な情報を設定 */
    updateColumns() {
      const igOnCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igOnCnt = this.$t('message.header_vehicle_dtc_table_igon_counts');
      const igOnCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnCntVar;
      const igOnTimeVar = util.mkHeaderValue(this.recollectedData.current_igon_mill_sec);
      const igOnTime = this.$t('message.header_vehicle_dtc_table_igon_times');
      const igOnTimeValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igOnTimeVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igonCount', label: util.setColums(igOnCnt, igOnCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon }, // ハイフンは翻訳リソースを使わない
        { name: 'igonTime', label: util.setColums(igOnTime, igOnTimeValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon }, // ハイフンは翻訳リソースを使わない
        { name: 'counterType', label: 'message.header_vehicle_dtc_table_type' },
        { name: 'ffd', label: 'message.header_vehicle_dtc_table_ffd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd }
      ];
    },

    /**
     * 初期表示で必要な情報を設定(RAB)
     */
    updateColumnsRAB() {
      const igonCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igonCnt = this.$t('message.header_vehicle_dtc_table_igon_counts');
      const igonCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igonCntVar;
      const igonTimeVar = util.mkHeaderValue(this.recollectedData.current_igon_mill_sec);
      const igonTime = this.$t('message.header_vehicle_dtc_table_igon_times');
      const igonTimeValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igonTimeVar;
      const absoTimeVar = util.mkHeaderValue(util.convertLocalDateTime(this.recollectedData.current_absolute_time));
      const absoTime = this.$t('individual.label_cnc_absolute_time');
      const absoTimeValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + absoTimeVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igonCount', label: util.setColums(igonCnt, igonCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'igonTime', label: util.setColums(igonTime, igonTimeValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'counterType', label: 'message.header_vehicle_dtc_table_type' },
        { name: 'absoluteTime', label: util.setColums(absoTime, absoTimeValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortDate },
        { name: 'ffd', label: 'message.header_vehicle_dtc_table_ffd', useButton: true, useResource: true, originalSortFunc: diagnoseResource.sortFuncFfd },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(ES)
     */
    updateColumnsES() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'lksMode', label: 'individual.label_cnc_lks_mode', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(ADAS)
     */
    updateColumnsADAS() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'lksMode', label: 'individual.label_cnc_lks_mode', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(ADAS2)
     */
    updateColumnsADAS2() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'accControlAccelerationStatus', label: 'individual.label_cnc_acc_control_acceleration_state', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(特殊機能ES)
     */
    updateColumnsSPES() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'lksMode', label: 'individual.label_cnc_lks_mode', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
        { name: 'gps', label: 'individual.label_cnc_gps', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'odometer', label: 'individual.label_cnc_odometer', originalSortFunc: diagnoseResource.sortFuncItems },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(特殊機能ADAS)
     */
    updateColumnsSPADAS() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'lksMode', label: 'individual.label_cnc_lks_mode', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
        { name: 'gps', label: 'individual.label_cnc_gps', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'odometer', label: 'individual.label_cnc_odometer', originalSortFunc: diagnoseResource.sortFuncItems },
      ];
    },

    /**
     * 初期表示で必要な情報を設定(特殊機能ADAS2)
     */
    updateColumnsSPADAS2() {
      const igCntVar = util.mkHeaderValue(this.recollectedData.current_igon_time);
      const igCnt = this.$t('message.header_vehicle_dtc_table_igcounter');
      const igCounterValue = this.$t('message.header_vehicle_dtc_table_measurement_value') + ': ' + igCntVar;
      this.measuredDataColumns = [
        { name: 'status', label: 'message.header_vehicle_dtc_table_status', originalSortFunc: this.sortStatus },
        { name: 'code', label: 'message.header_vehicle_dtc_table_code' },
        { name: 'description', label: 'individual.label_cnc_description_reason', originalSortFunc: this.sortDescription },
        { name: 'igCounter', label: util.setColums(igCnt, igCounterValue, "{0} {1}", true), useHtmlBind: true, originalSortFunc: diagnoseResource.sortIgon },
        { name: 'lksStatus', label: 'individual.label_cnc_lks_status', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'accControlAccelerationStatus', label: 'individual.label_cnc_acc_control_acceleration_state', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'occurrenceTime', label: 'individual.label_cnc_occurrence_time', originalSortFunc: diagnoseResource.sortDate },
        { name: 'gps', label: 'individual.label_cnc_gps', originalSortFunc: diagnoseResource.sortFuncItems },
        { name: 'odometer', label: 'individual.label_cnc_odometer', originalSortFunc: diagnoseResource.sortFuncItems },
      ];
    },

    /** ステータス(キャンセルコード)独自ソート */
    sortStatus(param) {
      if (!param.data) {
        return null;
      }

      const resultItems = util.sortItems(param.data, 'no', param.sortDir);
      return resultItems;
    },

    /** 説明・理由独自ソート */
    sortDescription(param) {
      if (!param.data) {
        return null;
      }

      // 結果リスト
      let resultItems = [];
      // 指定外コードリスト
      const unspecifiedList = [];
      // hyphenリスト
      const hyphenList = [];
      // キャンセルコード割り当て有りリスト
      let descriptionLists = [];
      param.data.forEach(element => {
        if (element.descriptionKey.indexOf('message.status_vehicle_dtc_description_unspecified') > -1) {
          unspecifiedList.push(element);
        }
        if (element.descriptionKey.indexOf('message.common_hyphen') > -1) {
          hyphenList.push(element);
        }
        // キャンセルコードの説明かどうかを判定
        if (element.descriptionKey.indexOf('cnc') > -1) {
          descriptionLists.push(element);
        }
      });

      descriptionLists = util.sortItems(descriptionLists, 'description', param.sortDir);

      // 昇順の場合
      if (param.sortDir === config.ASC) {
        resultItems = descriptionLists.concat(unspecifiedList);
        resultItems = resultItems.concat(hyphenList);
        return resultItems;
      }
      // 降順の場合
      else {
        resultItems = hyphenList.concat(unspecifiedList);
        resultItems = resultItems.concat(descriptionLists);
        return resultItems;
      }
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData);
    },

    /**
     * FFD 一覧を表示
     * @param {Object} arg 選択されたデータ
     * @returns FFDモーダル表示
     */
    async openFFDModal(arg) {
      if (!arg) return;
      // タイトルの設定
      this.ffdSystem = this.$t(DIAG_SYSTEM + this.systemId);
      this.ffdDescription = this.$t(arg.descriptionKey);
      const status = arg.status ? ` (${arg.status}) ` : '';
      this.ffdTitle = arg.code + status;
      this.ffdCode = arg.code;
      const cache = this.cachedFFDs.filter(e => e.system === this.systemId && e.no === arg.no && e.measuredId === this.measuredId);
      if (cache.length > 0) {
        // キャッシュ有りならキャッシュ済みデータを表示
        this.ffdTableItems = cache[0].data;
        this.selectedCancelCode = arg;
        this.cancelCodeFfdModal = true;
      } else {
        try {
          const params = { region_info: this.cachedProjectDataSource };
          const endPoint = '/cancelCodes/' + this.projectId + '/' + this.measuredId + '/ffd/systems/' + this.systemId + '/codes/' + arg.no;
          // キャッシュなしなら FFD 一覧情報を取得
          this.processing = true;
          const res = await api.getParamCall(config.CNC, endPoint, params, this.operationScreen);
          handler.validate(handler.validateTypes.all, res, this, null, () => {
            // 共通 FFD テーブルコンポーネント向けに情報をセット
            this.ffdTableItems = res.data.data;
            this.selectedCancelCode = arg;
            // 同一 FFD の次回表示を高速化させるためキャッシュ
            this.cacheFFDStores([...this.cachedFFDs, { system: this.systemId, no: arg.no, data: res.data.data, measuredId: this.measuredId }]);
            // FFD テーブルを表示
            this.cancelCodeFfdModal = true;
          }, (result) => {
            if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          });
        } finally {
          this.processing = false;
        }
      }
    },

    /**
     * FFD詳細モーダルを非表示にする
     */
    closeFfdModal() {
      this.cancelCodeFfdModal = false;
    },

    /** DataRecollection.jsのDIDのテストデータ出力関数  */
    didTestDataExport() {
      this.$emit('didTestDataExport');
    },

    /**
     * DataRecollection.jsのbackToDetailsを呼び出し
     */
    backToDetails() {
      this.$emit('backToDetails');
    },
  },

  mounted() {
    // FFDのキャッシュクリア
    this.cacheFFDStores([]);
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedFFDs: state => state.cache.ffdStores,
      cachedDetails: state => state.cache.projectDetails,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /** 計測データ名 */
    measuredDataName() {
      let measuredDataName = '';
      if (this.cachedDetails?.measures) {
        const targetMeasurement = this.cachedDetails.measures[0][apiService.CNC].find(measure => measure.registered_at.toString() === this.measuredId);
        measuredDataName = targetMeasurement ? targetMeasurement.name : '';
      }
      return measuredDataName;
    }
  },
};